<script setup>
import { ref, computed, onMounted, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { fetchResponsibleStats } from "@/services/userFollowService"
import { useCurrentUser } from "@/composables/useCurrentUser"
import ResponsibleStatsBlock from "@/components/statistics/ResponsibleStatsBlock.vue"
import ResponsibleUsersTable from "@/components/statistics/ResponsibleUsersTable.vue"
import Button from "primevue/button"
import Skeleton from "primevue/skeleton"

const route = useRoute()
const router = useRouter()

const loading = ref(true)
const error = ref(null)
const rawData = ref(null)
const { isResponsible, load: loadUser, loading: userLoading } = useCurrentUser()

const isLoading = computed(() => loading.value || userLoading.value)
const payload = computed(() => {
  const d = rawData.value
  if (!d) return null
  return d.data ?? d
})

const groups = computed(() => {
  const g = payload.value?.groups
  if (!g) return []
  return Array.isArray(g) ? g : [g]
})
const services = computed(() => {
  const s = payload.value?.services
  if (!s) return []
  return Array.isArray(s) ? s : [s]
})

const hasGroups = computed(() => groups.value.length > 0)
const hasServices = computed(() => services.value.length > 0)

const showErrorMessage = computed(() => {
  return Boolean(error.value && error.value.response?.status !== 403)
})

// Tabs
const tabs = computed(() => {
  const list = []
  if (hasGroups.value) {
    list.push({
      route: "/statistics",
      label: "statistics.tab.groups",
    })
  }
  if (hasServices.value) {
    list.push({
      route: "/statistics#services",
      label: "statistics.tab.services",
    })
  }
  return list
})

const activeTab = ref(route.fullPath)

watch(
  () => route.fullPath,
  (newPath) => {
    activeTab.value = newPath
  },
)

const currentEntities = computed(() => {
  if (activeTab.value === "/statistics#services") {
    return { list: services.value, type: "service" }
  }
  return { list: groups.value, type: "groupe" }
})

const loadStats = async () => {
  loading.value = true
  error.value = null
  rawData.value = null
  try {
    const response = await fetchResponsibleStats()
    rawData.value = response ?? null
  } catch (e) {
    error.value = e
  } finally {
    loading.value = false
    // Auto-redirect si un seul type est disponible
    if (!hasGroups.value && hasServices.value && activeTab.value !== "/statistics#services") {
      router.replace("/statistics#services")
    } else if (hasGroups.value && !hasServices.value && activeTab.value === "/statistics#services") {
      router.replace("/statistics")
    }
  }
}

onMounted(() => {
  loadUser()
  loadStats()
})
</script>

<template>
  <div class="w-full p-4">
    <div class="mb-6">
      <h1 class="text-xl md:text-2xl lg:text-3xl font-bold text-white">
        {{ $t("statistics.title") }}
      </h1>
    </div>

    <template v-if="isLoading">
      <div class="bg-white rounded-2xl shadow-sm p-5 mb-6">
        <div class="flex items-center gap-3 mb-6">
          <Skeleton width="16rem" height="1.75rem" />
          <Skeleton width="5rem" height="1.5rem" class="rounded-full" />
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
          <div
            v-for="i in 5"
            :key="`kpi-${i}`"
            class="border border-gray-100 rounded-xl p-4"
          >
            <div class="flex items-center justify-between mb-3">
              <Skeleton width="3rem" height="2rem" />
              <Skeleton shape="circle" size="2rem" />
            </div>
            <Skeleton width="70%" height="0.8rem" />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div
            v-for="i in 3"
            :key="`camp-${i}`"
            class="border border-gray-100 rounded-xl p-4"
          >
            <div class="flex items-center justify-between mb-2">
              <Skeleton width="2rem" height="2rem" />
              <Skeleton shape="circle" size="2rem" />
            </div>
            <Skeleton width="60%" height="0.8rem" class="mb-2" />
            <Skeleton width="90%" height="0.7rem" />
          </div>
        </div>

        <Skeleton width="10rem" height="0.9rem" class="mb-3" />
        <Skeleton height="10rem" class="rounded-xl" />
      </div>

      <div class="bg-white rounded-2xl shadow-sm p-5 mb-10">
        <Skeleton width="12rem" height="0.9rem" class="mb-3" />
        <Skeleton height="14rem" class="rounded-xl" />
      </div>

      <p class="text-sm text-gray-300 text-center mt-4">
        {{ $t("statistics.loading") }}
      </p>
    </template>

    <template v-else-if="showErrorMessage">
      <div class="p-5 rounded-xl border border-red-100 bg-red-50 text-red-600 text-sm">
        {{ $t("statistics.error_load") }}
      </div>
    </template>

    <template v-else-if="!isResponsible">
      <div class="p-5 rounded-xl border border-gray-100 bg-gray-50 text-gray-500 text-sm">
        {{ $t("statistics.responsible_only") }}
      </div>
    </template>

    <template v-else>
      <div
        v-if="tabs.length > 1"
        class="flex flex-wrap gap-2 mb-8"
      >
        <Button
          asChild
          v-for="tab in tabs"
          :key="tab.route"
          custom
          class="w-full sm:w-auto"
        >
          <router-link
            :to="tab.route"
            :class="[
              'px-3 md:px-6 py-2 font-semibold rounded-full transition flex items-center gap-2 text-sm md:text-base w-full justify-center sm:w-auto',
              tab.route == activeTab
                ? 'bg-white primary-text shadow'
                : 'text-white bg-transparent hover:bg-white/20',
            ]"
          >
            <span>{{ $t(tab.label) }}</span>
          </router-link>
        </Button>
      </div>

      <div v-if="currentEntities.list.length > 0">
        <template v-for="entity in currentEntities.list" :key="entity.id">
          <ResponsibleStatsBlock
            :entity="entity"
            :entity-type="currentEntities.type"
          />

          <div class="bg-white rounded-2xl shadow-sm p-5 mb-10">
            <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
              {{ $t("statistics.users_list") }}
            </h3>
            <ResponsibleUsersTable :users="entity.stats?.users || []" />
          </div>
        </template>
      </div>

      <div
        v-else
        class="p-5 rounded-xl border border-gray-100 bg-gray-50 text-gray-500 text-sm"
      >
        {{ $t("statistics.no_data") }}
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use "../../../assets/scss/variables.scss";
@use "../../../assets/scss/app/common.scss";

.primary-text {
  color: #1f2937;
}
</style>
